/**
 * Toast Component
 * Simple toast notification that appears and auto-dismisses
 * Uses React portal to render at document body level
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

export interface ToastProps {
  message: string;
  type?: 'error' | 'success' | 'info';
  duration?: number;
  onClose?: () => void;
}

export function Toast({
  message,
  type = 'error',
  duration = 4000,
  onClose,
}: ToastProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLeaving(true);
      setTimeout(() => {
        setIsVisible(false);
        onClose?.();
      }, 300); // Animation duration
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        'toast',
        `toast--${type}`,
        isLeaving && 'toast--leaving'
      )}
    >
      <span className="toast__icon">
        {type === 'error' && '✕'}
        {type === 'success' && '✓'}
        {type === 'info' && 'ℹ'}
      </span>
      <span className="toast__message">{message}</span>
    </div>
  );
}

// Hook for managing toasts
export interface ToastState {
  message: string;
  type: 'error' | 'success' | 'info';
  id: number;
}

let toastId = 0;

export function useToast() {
  const [toasts, setToasts] = useState<ToastState[]>([]);

  const showToast = (message: string, type: 'error' | 'success' | 'info' = 'error') => {
    const id = ++toastId;
    setToasts((prev) => [...prev, { message, type, id }]);
  };

  const removeToast = (id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const showError = (message: string) => showToast(message, 'error');
  const showSuccess = (message: string) => showToast(message, 'success');
  const showInfo = (message: string) => showToast(message, 'info');

  return {
    toasts,
    showToast,
    showError,
    showSuccess,
    showInfo,
    removeToast,
  };
}

// Toast container component - uses portal to render at body level
export function ToastContainer({ toasts, onRemove }: {
  toasts: ToastState[];
  onRemove: (id: number) => void;
}) {
  // Don't render if no toasts
  if (toasts.length === 0) return null;

  // Use portal to render at body level (avoids overflow/z-index issues)
  return createPortal(
    <div className="toast-container">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => onRemove(toast.id)}
        />
      ))}
    </div>,
    document.body
  );
}
